/**
 * The per-Level sequence (issue #9): for every Level up to the Character's, the Choices and
 * Answers the guide walks, in the documented order. Level 1: ancestry, heritage, background,
 * class, attribute boosts (ancestry, background, class key, four free), then the Choices the
 * class and ancestry open at 1 (class feat, ancestry feat, skill trainings, class-specific
 * ChoiceSets, the deity a class asks for). Later Levels: the progression table's Choices, the
 * Variant Rules' extras, then the ChoiceSets of the Entries taken or granted at that Level.
 */
import type { Choice, ChoiceSource, FoundationKind, LevelNumber, ProgressionSlot, Reopened } from "../character/model";
import { choiceKey } from "../character/model";
import { skillPotencyStep } from "../engine/abp";
import type { ChoicePrompt } from "../engine/engine";
import type { Attribute } from "../engine/values";
import { slotOptions } from "./candidates";
import type { GuideContext } from "./context";
import { levelOf } from "./context";
import { expectedSlots, intelligenceRises, isAnswerSlot } from "./slots";

export type GuideItem = EntryItem | AnswerItem | AttributeItem | SkillItem | FlagItem;

interface ItemBase {
  /** Unique within the Character: the Choice key, plus the flag for an Answer. */
  id: string;
  /** The Choice the item lives on. */
  choiceKey: string;
  level: LevelNumber;
  label: string;
  /** Nothing chosen yet and something is required. */
  open: boolean;
  /** An optional item never counts as open. */
  optional: boolean;
  /** Advice shown beside the item (an unexpected slot, a note from the engine). */
  warning: string | null;
}

/** A Choice filled with an Entry: a foundation or a feat slot. */
export interface EntryItem extends ItemBase {
  kind: "entry";
  source: ChoiceSource;
  slot: ProgressionSlot | null;
  selection: { uuid: string; name: string; remaster: boolean | null } | null;
  /** The Selection is no longer structurally legal (its class changed, say). */
  illegal: string | null;
}

/** A ChoiceSet on a Selection's Entry, answered on the Choice. */
export interface AnswerItem extends ItemBase {
  kind: "answer";
  flag: string;
  prompt: ChoicePrompt;
  entryName: string;
}

/** An attribute answer: a free boost, an ancestry or background boost slot, the class key attribute, a voluntary flaw, the ABP apex. */
export interface AttributeItem extends ItemBase {
  kind: "attribute";
  flag: string;
  /** Which candidate rule applies. */
  role: "free" | "ancestry" | "background" | "key" | "voluntary-flaw" | "voluntary-boost" | "alternate" | "apex";
  value: Attribute | null;
  partial: boolean;
}

/** A skill answer: a class training at Level 1, a skill increase, or an ABP skill potency step. */
export interface SkillItem extends ItemBase {
  kind: "skill";
  flag: string;
  role: "training" | "increase" | "potency";
  intelligence: boolean;
  value: string | null;
}

/** A boolean answer: alternate ancestry boosts (two free boosts instead of the listed ones). */
export interface FlagItem extends ItemBase {
  kind: "flag";
  flag: string;
  value: boolean;
}

export interface GuideLevel {
  level: LevelNumber;
  items: GuideItem[];
  /** How many items are open. */
  open: number;
}

const SLOT_LABELS: Record<ProgressionSlot, string> = {
  "ancestry-feat": "Ancestry feat",
  "class-feat": "Class feat",
  "class-feature": "Class feature",
  "general-feat": "General feat",
  "skill-feat": "Skill feat",
  "skill-increase": "Skill increase",
  "attribute-boosts": "Attribute boost",
  "archetype-feat": "Archetype feat (Free Archetype)",
  "ancestry-paragon-feat": "Ancestry feat (Ancestry Paragon)",
  "dual-class-feat": "Class feat (Dual Class)",
  "skill-potency": "Skill potency (Automatic Bonus Progression)",
  "attribute-apex": "Attribute apex (Automatic Bonus Progression)",
  "familiar-ability": "Familiar ability",
  apparition: "Apparition",
};

export function slotLabel(slot: ProgressionSlot): string {
  return SLOT_LABELS[slot];
}

const FOUNDATION_LABELS = { ancestry: "Ancestry", heritage: "Heritage", background: "Background", class: "Class", "dual-class": "Second class (Dual Class)" } as const;
const ATTRIBUTE_LABELS: Record<Attribute, string> = { str: "Strength", dex: "Dexterity", con: "Constitution", int: "Intelligence", wis: "Wisdom", cha: "Charisma" };

/** The guide for every Level up to the Character's. */
export function guideLevels(ctx: GuideContext): GuideLevel[] {
  const out: GuideLevel[] = [];
  for (const level of ctx.character.levels) {
    if (level.number > ctx.character.level) break;
    const items = level.number === 1 ? levelOneItems(ctx, level.choices) : laterItems(ctx, level.number, level.choices);
    for (const choice of level.choices) {
      if (!choice.reopened) continue;
      const key = choiceKey(level.number, choice.source);
      for (const item of items) {
        if (item.choiceKey !== key) continue;
        const flag = "flag" in item ? item.flag : null;
        const record = choice.reopened.find((r) => r.flag === flag);
        if (record) item.warning = item.warning ? `${reopenedWarning(record)}; ${item.warning}` : reopenedWarning(record);
      }
    }
    out.push({ level: level.number, items, open: items.filter((i) => i.open).length });
  }
  return out;
}

/** "reopened by the Snapshot update to pf2e-8.6.0: Vision of Weakness is no longer in the Snapshot" (#17). */
export function reopenedWarning(r: Reopened): string {
  const what = r.name ?? r.uuid ?? "the previous Selection";
  return r.reason === "missing"
    ? `reopened by the Snapshot update to ${r.to}: ${what} is no longer in the Snapshot`
    : `reopened by the Snapshot update to ${r.to}: ${what}: ${r.detail ?? "no longer legal"}`;
}

/** Every open item across the Character: the badge. */
export function openItems(ctx: GuideContext): GuideItem[] {
  return guideLevels(ctx).flatMap((l) => l.items.filter((i) => i.open));
}

function nameOf(ctx: GuideContext, uuid: string): { name: string; remaster: boolean | null } {
  const entry = ctx.entries.get(uuid);
  if (entry) return { name: entry.name, remaster: entry.publication.remaster };
  const record = ctx.catalogue.byUuid(uuid);
  return { name: record?.n ?? uuid, remaster: record?.rm ?? null };
}

function entryItem(ctx: GuideContext, level: LevelNumber, choice: Choice, label: string, slot: ProgressionSlot | null): EntryItem {
  const key = choiceKey(level, choice.source);
  const selection = choice.selection ? { uuid: choice.selection.uuid, ...nameOf(ctx, choice.selection.uuid) } : null;
  const missing = selection && ctx.sheet.missing.some((m) => m.choiceKey === key) ? "this Entry is not in the Snapshot" : null;
  const extra = ctx.extra.has(key) ? "not opened by the current class tables" : null;
  return { kind: "entry", id: key, choiceKey: key, level, label, open: selection === null, optional: false, warning: missing ?? extra, source: choice.source, slot, selection, illegal: null };
}

function progressionItem(ctx: GuideContext, level: LevelNumber, choice: Choice, intelligence: boolean): GuideItem {
  const source = choice.source as Extract<ChoiceSource, { kind: "progression" }>;
  const key = choiceKey(level, source);
  const answers = choice.answers ?? {};
  if (source.slot === "attribute-boosts") {
    const value = typeof answers.boost === "string" ? (answers.boost as Attribute) : null;
    return { kind: "attribute", id: key, choiceKey: key, level, label: `Attribute boost ${source.ordinal + 1}`, open: value === null, optional: false, warning: ctx.extra.has(key) ? "not opened by the current tables" : null, flag: "boost", role: "free", value, partial: value ? ctx.track.isPartial(level, source.ordinal, value) : false };
  }
  if (source.slot === "skill-increase") {
    const value = typeof answers.skill === "string" && answers.skill ? answers.skill : null;
    return { kind: "skill", id: key, choiceKey: key, level, label: intelligence ? "Skill training (Intelligence)" : "Skill increase", open: value === null, optional: false, warning: ctx.extra.has(key) ? "not opened by the current tables" : null, flag: "skill", role: "increase", intelligence, value };
  }
  if (source.slot === "skill-potency") {
    const value = typeof answers.skill === "string" && answers.skill ? answers.skill : null;
    const step = skillPotencyStep(level, source.ordinal);
    const label = step?.kind === "raise" ? `Skill potency: raise a +${step.from} to +${step.from + 1}` : "Skill potency: a new +1";
    return { kind: "skill", id: key, choiceKey: key, level, label, open: value === null, optional: false, warning: ctx.extra.has(key) ? "not opened by the current tables" : null, flag: "skill", role: "potency", intelligence: false, value };
  }
  if (source.slot === "attribute-apex") {
    const value = attributeAnswer(answers.attribute);
    return { kind: "attribute", id: key, choiceKey: key, level, label: "Attribute apex", open: value === null, optional: false, warning: ctx.extra.has(key) ? "not opened by the current tables" : null, flag: "attribute", role: "apex", value, partial: false };
  }
  return entryItem(ctx, level, choice, SLOT_LABELS[source.slot], source.slot);
}

/** The ChoiceSets on the Entries held by Choices at this Level, answered or open, after the Choice they belong to. */
function promptItems(ctx: GuideContext, level: LevelNumber, forKey?: string): AnswerItem[] {
  const out: AnswerItem[] = [];
  for (const prompt of ctx.sheet.prompts) {
    if (!prompt.choiceKey || levelOf(prompt.choiceKey) !== level) continue;
    if (forKey !== undefined && prompt.choiceKey !== forKey) continue;
    if (prompt.note === "preselected by the granting Entry") continue;
    const open = prompt.open;
    const optional = prompt.allowNoSelection && !open;
    const warning = prompt.available === false ? (prompt.note ?? "candidates are not available yet") : prompt.legal === false ? (prompt.note ?? "the answer is not among the candidates") : prompt.note && open ? prompt.note : null;
    out.push({ kind: "answer", id: `${prompt.choiceKey}#${prompt.flag}`, choiceKey: prompt.choiceKey, level, label: prompt.prompt.replace(/\.$/, ""), open, optional, warning, flag: prompt.flag, prompt, entryName: prompt.name });
  }
  return out;
}

function levelOneItems(ctx: GuideContext, choices: Choice[]): GuideItem[] {
  const byKind = (kind: FoundationKind): Choice | undefined => choices.find((c) => c.source.kind === kind);
  const items: GuideItem[] = [];
  const foundations: FoundationKind[] = ["ancestry", "heritage", "background", "class"];
  // The Dual Class second class (#12) follows the first while the rule is on, or while it still holds a Selection.
  const dual = byKind("dual-class");
  if (ctx.character.variantRules.dualClass || dual?.selection) foundations.push("dual-class");
  for (const kind of foundations) {
    const choice = byKind(kind) ?? { source: { kind } };
    const item = entryItem(ctx, 1, choice, FOUNDATION_LABELS[kind], null);
    if (kind === "dual-class" && !ctx.character.variantRules.dualClass) item.warning = "Dual Class is off";
    items.push(item);
    if (kind === "heritage" || kind === "background") items.push(...promptItems(ctx, 1, choiceKey(1, { kind })));
  }
  // Boosts: ancestry, background, class key, then the four free ones.
  items.push(...ancestryBoostItems(ctx, byKind("ancestry")));
  items.push(...backgroundBoostItems(ctx, byKind("background")));
  items.push(...keyAbilityItems(ctx, byKind("class")));
  const progression = choices.filter((c): c is Choice & { source: Extract<ChoiceSource, { kind: "progression" }> } => c.source.kind === "progression").sort(bySlotOrder);
  for (const c of progression) if (c.source.slot === "attribute-boosts") items.push(progressionItem(ctx, 1, c, false));
  for (const c of progression) if (c.source.slot !== "attribute-boosts" && c.source.slot !== "skill-increase") items.push(progressionItem(ctx, 1, c, false));
  items.push(...skillTrainingItems(ctx, byKind("class")));
  for (const c of progression) if (c.source.slot === "skill-increase") items.push(progressionItem(ctx, 1, c, false));
  // Class-specific ChoiceSets (the class's own, then the features granted at 1), the second class's, the ancestry's.
  items.push(...promptItems(ctx, 1, choiceKey(1, { kind: "class" })));
  items.push(...promptItems(ctx, 1, choiceKey(1, { kind: "dual-class" })));
  items.push(...promptItems(ctx, 1, choiceKey(1, { kind: "ancestry" })));
  const seen = new Set(items.map((i) => i.id));
  for (const p of promptItems(ctx, 1)) if (!seen.has(p.id)) items.push(p);
  return items;
}

function laterItems(ctx: GuideContext, level: LevelNumber, choices: Choice[]): GuideItem[] {
  const items: GuideItem[] = [];
  const expected = expectedSlots(level, ctx.tables, ctx.character.variantRules, intelligenceRises(ctx.track, level));
  const intelligenceKeys = new Set(expected.filter((s) => s.intelligence).map((s) => choiceKey(level, { kind: "progression", slot: s.slot, ordinal: s.ordinal })));
  const progression = choices.filter((c): c is Choice & { source: Extract<ChoiceSource, { kind: "progression" }> } => c.source.kind === "progression").sort(bySlotOrder);
  for (const c of progression) {
    const key = choiceKey(level, c.source);
    items.push(progressionItem(ctx, level, c, intelligenceKeys.has(key)));
    if (!isAnswerSlot(c.source.slot)) items.push(...promptItems(ctx, level, key));
  }
  const seen = new Set(items.map((i) => i.id));
  for (const p of promptItems(ctx, level)) if (!seen.has(p.id)) items.push(p);
  return items;
}

const SLOT_ORDER: ProgressionSlot[] = ["attribute-boosts", "attribute-apex", "class-feat", "dual-class-feat", "ancestry-feat", "ancestry-paragon-feat", "skill-feat", "general-feat", "skill-increase", "skill-potency", "archetype-feat", "familiar-ability", "apparition", "class-feature"];

function bySlotOrder(a: Choice & { source: { slot: ProgressionSlot; ordinal: number } }, b: Choice & { source: { slot: ProgressionSlot; ordinal: number } }): number {
  return SLOT_ORDER.indexOf(a.source.slot) - SLOT_ORDER.indexOf(b.source.slot) || a.source.ordinal - b.source.ordinal;
}

function attributeItem(key: string, flag: string, label: string, role: AttributeItem["role"], value: Attribute | null, optional = false): AttributeItem {
  return { kind: "attribute", id: `${key}#${flag}`, choiceKey: key, level: 1, label, open: value === null && !optional, optional, warning: null, flag, role, value, partial: false };
}

/** The ancestry's boost slots that offer a choice, the alternate-boosts switch, and the voluntary flaws. */
function ancestryBoostItems(ctx: GuideContext, choice: Choice | undefined): GuideItem[] {
  const uuid = choice?.selection?.uuid;
  const entry = uuid ? ctx.entries.get(uuid) : undefined;
  if (!choice || !entry) return [];
  const key = choiceKey(1, { kind: "ancestry" });
  const answers = choice.answers ?? {};
  const items: GuideItem[] = [];
  const slots = slotOptions(entry.system.boosts);
  const alternate = answers.alternateBoosts === true;
  const hasFixed = slots.some((s) => s.length === 1);
  if (hasFixed) items.push({ kind: "flag", id: `${key}#alternateBoosts`, choiceKey: key, level: 1, label: "Alternate ancestry boosts: two free boosts instead of the listed ones", open: false, optional: true, warning: null, flag: "alternateBoosts", value: alternate });
  if (alternate) {
    for (const flag of ["alternate:0", "alternate:1"]) items.push(attributeItem(key, flag, `${entry.name} boost (alternate)`, "alternate", attributeAnswer(answers[flag])));
  } else {
    slots.forEach((options, i) => {
      if (options.length <= 1) return;
      items.push(attributeItem(key, `boost:${i}`, `${entry.name} free boost`, "ancestry", attributeAnswer(answers[`boost:${i}`])));
    });
  }
  const flaws = slotOptions(entry.system.flaws);
  flaws.forEach((options, i) => {
    if (options.length > 1) items.push(attributeItem(key, `flaw:${i}`, `${entry.name} flaw`, "ancestry", attributeAnswer(answers[`flaw:${i}`])));
  });
  // Voluntary flaws: two flaws for one extra boost.
  items.push(attributeItem(key, "voluntary:flaw:0", "Voluntary flaw (optional)", "voluntary-flaw", attributeAnswer(answers["voluntary:flaw:0"]), true));
  const firstFlaw = attributeAnswer(answers["voluntary:flaw:0"]);
  if (firstFlaw) {
    items.push(attributeItem(key, "voluntary:flaw:1", "Second voluntary flaw", "voluntary-flaw", attributeAnswer(answers["voluntary:flaw:1"])));
    items.push(attributeItem(key, "voluntary:boost", "Boost for the voluntary flaws", "voluntary-boost", attributeAnswer(answers["voluntary:boost"])));
  }
  return items;
}

function backgroundBoostItems(ctx: GuideContext, choice: Choice | undefined): GuideItem[] {
  const uuid = choice?.selection?.uuid;
  const entry = uuid ? ctx.entries.get(uuid) : undefined;
  if (!choice || !entry) return [];
  const key = choiceKey(1, { kind: "background" });
  const answers = choice.answers ?? {};
  return slotOptions(entry.system.boosts).flatMap((options, i) => (options.length > 1 ? [attributeItem(key, `boost:${i}`, `${entry.name} boost`, "background", attributeAnswer(answers[`boost:${i}`]))] : []));
}

function keyAbilityItems(ctx: GuideContext, choice: Choice | undefined): GuideItem[] {
  if (!choice?.selection) return [];
  const key = choiceKey(1, { kind: "class" });
  const options = [...new Set([...ctx.tables.keyOptions, ...ctx.state.actor.system.build.attributes.keyOptions])];
  if (options.length <= 1) return [];
  const value = attributeAnswer(choice.answers?.keyAbility);
  return [attributeItem(key, "keyAbility", "Key attribute", "key", value)];
}

/** The class's free skill trainings: `additional` plus the Intelligence modifier at Level 1. */
function skillTrainingItems(ctx: GuideContext, choice: Choice | undefined): GuideItem[] {
  if (!choice?.selection) return [];
  const key = choiceKey(1, { kind: "class" });
  const count = ctx.tables.additionalSkills + Math.max(0, ctx.track.modAt(1).int);
  const answers = choice.answers ?? {};
  const items: SkillItem[] = [];
  const held = Object.keys(answers).filter((k) => /^skill:\d+$/.test(k)).map((k) => Number(k.split(":")[1]));
  const total = Math.max(count, held.length > 0 ? Math.max(...held) + 1 : 0);
  for (let i = 0; i < total; i++) {
    const flag = `skill:${i}`;
    const value = typeof answers[flag] === "string" && answers[flag] ? (answers[flag] as string) : null;
    const beyond = i >= count;
    items.push({ kind: "skill", id: `${key}#${flag}`, choiceKey: key, level: 1, label: `Trained skill ${i + 1}`, open: value === null && !beyond, optional: beyond, warning: beyond ? "beyond the class's trainings at the current Intelligence" : null, flag, role: "training", intelligence: false, value });
  }
  return items;
}

function attributeAnswer(value: unknown): Attribute | null {
  return typeof value === "string" && value in ATTRIBUTE_LABELS ? (value as Attribute) : null;
}

export { ATTRIBUTE_LABELS };
